import { ReactComponent as InfoIcon } from 'assets/images/icons/info.svg';
import { ReactComponent as ObjectIcon } from 'assets/images/icons/object.svg';
import { ReactComponent as ZoneIcon } from 'assets/images/icons/zone.svg';
import noImage from 'assets/images/no-image.png';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ZoneAiAnalysis } from 'entities/common';

import Badge from 'ui/Badge';
import Loader from 'ui/Loader';

import SensorCard from 'modules/ZoneDetails/SensorCard';

import { plantsApi, PlantSearchResult } from 'services/plants';
import {
  getFilePresignedUrl,
  getObjectById,
  getObjectSensors,
  getZoneAiAnalysisDay,
  getZoneAiAnalysisLatest,
  getZoneById,
  getZoneSensors,
} from 'services/sensors';
import { SOIL_TYPE } from 'utils/constants';
import { formatLocalClock, formatLocalDateTime, getBrowserTimeZone, parseServerDate } from 'utils/helpers';

type ZoneData = {
  name: string;
  plant_type: string | null;
  plant_name: string | null;
  soil_type: string | null;
  object_id: number;
  photo_file_id: string | null;
};

type SensorItem = {
  id: number;
  sensor_type: string;
  active: boolean;
  is_deleted: boolean;
  latest_metric_time: string | null;
};

type ZoneAiDaySlot = {
  hour_start: string;
  hour_end: string;
  available: boolean;
  analysis_text: string;
};

type ZoneStatus = 'Healthy' | 'Issue detected' | 'Needs attention' | 'No information';

function computeZoneStatus(zoneAiAnalysis: ZoneAiAnalysis | null): ZoneStatus {
  if (zoneAiAnalysis) {
    const status = zoneAiAnalysis.status?.toLowerCase();
    if (status === 'red') return 'Issue detected';
    if (status === 'yellow') return 'Needs attention';
    if (status === 'blue') return 'Healthy';
  }

  return 'No information';
}

const OBJECT_SENSOR_TYPE_ORDER: Record<string, number> = {
  'SenseCAP S800': 0,
  'SenseCAP S2102': 1,
  'SenseCAP S2103': 2,
};

const ZONE_SENSOR_TYPE_ORDER: Record<string, number> = {
  'SenseCAP S2104': 0,
  'SenseCAP S2105': 1,
  'SenseCAP S2108': 2,
};

function sortSensorsByType(sensors: SensorItem[], order: Record<string, number>): SensorItem[] {
  return [...sensors].sort((a, b) => {
    const orderA = order[a.sensor_type] ?? 999;
    const orderB = order[b.sensor_type] ?? 999;
    return orderA - orderB;
  });
}

function formatSnakeCase(value: string | null): string {
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getLatestUpdate(sensors: SensorItem[]): string {
  let latestIso: string | null = null;
  let latestMs = -Infinity;

  for (const sensor of sensors) {
    if (sensor.latest_metric_time && sensor.active && !sensor.is_deleted) {
      const ms = parseServerDate(sensor.latest_metric_time).valueOf();
      if (!Number.isNaN(ms) && ms > latestMs) {
        latestMs = ms;
        latestIso = sensor.latest_metric_time;
      }
    }
  }

  if (!latestIso) return '-';

  return formatLocalDateTime(latestIso);
}

function getStatusBadgeClass(status: ZoneStatus): string {
  switch (status) {
    case 'Healthy':
      return 'zone-details__badge--healthy';
    case 'Needs attention':
      return 'zone-details__badge--attention';
    case 'Issue detected':
      return 'zone-details__badge--issue';
    default:
      return 'zone-details__badge--noinfo';
  }
}

function getZoneCardModifier(status: ZoneStatus): string {
  switch (status) {
    case 'Healthy':
      return 'zone-details__zone-card--healthy';
    case 'Needs attention':
      return 'zone-details__zone-card--attention';
    case 'Issue detected':
      return 'zone-details__zone-card--issue';
    default:
      return 'zone-details__zone-card--noinfo';
  }
}

export const ZoneDetails: React.FC = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [zone, setZone] = useState<ZoneData | null>(null);
  const [objectName, setObjectName] = useState<string>('-');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [zoneSensors, setZoneSensors] = useState<SensorItem[]>([]);
  const [objectSensors, setObjectSensors] = useState<SensorItem[]>([]);
  const [zoneStatus, setZoneStatus] = useState<ZoneStatus>('No information');
  const [zoneAiAnalysis, setZoneAiAnalysis] = useState<ZoneAiAnalysis | null>(null);
  const [displayedAnalysisText, setDisplayedAnalysisText] = useState<string | null>(null);
  const [daySlots, setDaySlots] = useState<ZoneAiDaySlot[]>([]);
  const [daySlotsDate, setDaySlotsDate] = useState<string | null>(null);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const latestAnalysisText = useRef<string | null>(null);
  const [plantList, setPlantList] = useState<PlantSearchResult[]>([]);

  const handleHourClick = useCallback(
    async (hour: string, chartDate: Date) => {
      if (!id) return;
      const zoneId = parseInt(id);
      if (isNaN(zoneId)) return;

      // If clicking the same hour again, reset to latest
      if (selectedHour === hour) {
        setSelectedHour(null);
        setDisplayedAnalysisText(latestAnalysisText.current);
        return;
      }

      setSelectedHour(hour);

      const pad = (n: number) => String(n).padStart(2, '0');
      const localDay = `${chartDate.getFullYear()}-${pad(chartDate.getMonth() + 1)}-${pad(
        chartDate.getDate(),
      )}`;
      const targetIso = new Date(
        chartDate.getFullYear(),
        chartDate.getMonth(),
        chartDate.getDate(),
        12,
        0,
        0,
      ).toISOString();
      const tz = getBrowserTimeZone();
      let slots = daySlots;

      if (slots.length === 0 || daySlotsDate !== localDay) {
        try {
          const res = await getZoneAiAnalysisDay(
            zoneId,
            `?target_date=${encodeURIComponent(targetIso)}&tz=${encodeURIComponent(tz)}`,
          );
          slots = res.data?.slots || [];
          setDaySlots(slots);
          setDaySlotsDate(localDay);
        } catch {
          setDisplayedAnalysisText(null);
          return;
        }
      }

      const slot = (slots || [])?.find((s) => formatLocalClock(s.hour_start) === hour);

      if (slot?.available && slot.analysis_text) {
        setDisplayedAnalysisText(slot.analysis_text);
      } else {
        setDisplayedAnalysisText(null);
      }
    },
    [id, selectedHour, daySlots, daySlotsDate],
  );

  useEffect(() => {
    if (!id) return;

    const zoneId = parseInt(id);
    if (isNaN(zoneId)) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const zoneResponse = await getZoneById(zoneId);
        const zoneData: ZoneData = zoneResponse.data;
        setZone(zoneData);

        // Fetch object name, photo URL, and sensors in parallel
        const promises: Promise<any>[] = [
          getZoneSensors(zoneId),
          zoneData.object_id ? getObjectSensors(zoneData.object_id) : Promise.resolve({ data: [] }),
          zoneData.object_id
            ? getObjectById(zoneData.object_id)
            : Promise.resolve({ data: { name: '-' } }),
        ];

        if (zoneData.photo_file_id) {
          promises.push(getFilePresignedUrl(zoneData.photo_file_id).catch(() => null));
        }

        const [zoneSensorsRes, objectSensorsRes, objectRes, photoRes] = await Promise.all(promises);

        setObjectName(objectRes?.data?.name || '-');

        if (photoRes?.data?.url) {
          setPhotoUrl(photoRes.data.url);
        }

        const zoneSensorsData: SensorItem[] = zoneSensorsRes.data;
        const objectSensorsData: SensorItem[] = objectSensorsRes.data;
        const allSensors = [...zoneSensorsData, ...objectSensorsData];

        setZoneSensors(sortSensorsByType(zoneSensorsData, ZONE_SENSOR_TYPE_ORDER));
        setObjectSensors(sortSensorsByType(objectSensorsData, OBJECT_SENSOR_TYPE_ORDER));

        // Check if any active, non-deleted sensor has metrics
        const activeSensors = allSensors.filter((s) => s.active && !s.is_deleted);
        const hasAnyMetrics =
          activeSensors.length > 0 && activeSensors?.some((s) => s.latest_metric_time);

        // Fetch zone AI analysis only if sensors have metrics
        let aiAnalysis: ZoneAiAnalysis | null = null;
        if (hasAnyMetrics) {
          try {
            const aiRes = await getZoneAiAnalysisLatest(zoneId);
            aiAnalysis = aiRes.data;
            setZoneAiAnalysis(aiAnalysis);
            latestAnalysisText.current = aiAnalysis?.analysis_text || null;
            setDisplayedAnalysisText(aiAnalysis?.analysis_text || null);
          } catch {
            // AI analysis not available yet — not an error
            latestAnalysisText.current = null;
            setDisplayedAnalysisText(null);
          }
        } else {
          latestAnalysisText.current = null;
          setDisplayedAnalysisText(null);
        }

        setZoneStatus(computeZoneStatus(aiAnalysis));
      } catch (error) {
        console.error('Failed to fetch zone details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (zone?.plant_type) {
      plantsApi.list(zone.plant_type, zone?.plant_name || undefined).then((res) => {
        setPlantList(res.results);
      });
    }
  }, [zone?.plant_type, zone?.plant_name]);

  const plantName = useMemo(() => {
    return (
      (plantList || [])?.find(
        (plant) => plant?.value?.replace(/[_-]/g, '') === zone?.plant_name?.replace(/[_-]/g, ''),
      )?.label || '-'
    );
  }, [plantList, zone?.plant_name]);

  const data = useMemo(() => {
    return [
      { label: 'Plant type', value: formatSnakeCase(zone?.plant_type || '-') },
      { label: 'Plant name', value: plantName || '-' },
      {
        label: 'Soil type',
        value: SOIL_TYPE?.find((soil) => soil.value === zone?.soil_type)?.label || '-',
      },
      { label: 'Sensors', value: zoneSensors?.length + objectSensors?.length || 0 },
      {
        label: 'Last update',
        value: getLatestUpdate([...(zoneSensors || []), ...(objectSensors || [])]),
      },
    ];
  }, [zone, zoneSensors, objectSensors, plantName]);

  if (isLoading) {
    return <Loader isLoading isGlobal />;
  }

  if (!zone) {
    return null;
  }

  return (
    <div className="zone-details">
      <div className="zone-details__content">
        {/* Zone Card */}
        <div className={classNames('zone-details__zone-card', getZoneCardModifier(zoneStatus))}>
          {(photoUrl || !zone.photo_file_id) && (
            <img
              className="zone-details__zone-card-photo"
              src={photoUrl || noImage}
              alt={zone.name}
            />
          )}
          <div className="zone-details__zone-card-header">
            <div className="zone-details__zone-card-name-area">
              <div className="zone-details__zone-card-name">{zone.name}</div>
              <div className="zone-details__zone-card-location">{objectName}</div>
            </div>
            <Badge type="info" title={zoneStatus} className={getStatusBadgeClass(zoneStatus)} />
          </div>

          <div className="zone-details__zone-card-details">
            {data.map((item) => (
              <div key={item.label} className="zone-details__zone-card-detail">
                <div className="zone-details__zone-card-detail-label">{item.label}</div>
                <div className="zone-details__zone-card-detail-value">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="zone-details__info-box zone-details__info-box--disclaimer">
          <InfoIcon className="zone-details__info-box-icon" />
          <div className="zone-details__info-box-content">
            <div className="zone-details__info-box-title">Disclaimer</div>
            <p className="zone-details__info-box-text">
              AI Insights is generated automatically based on available zone, sensor, and
              environmental data. It is provided for informational purposes only and does not
              constitute professional advice. Users are responsible for verifying the information
              and making their own decisions.
            </p>
          </div>
        </div>

        {/* AI Insights */}
        <div className="zone-details__info-box zone-details__info-box--insights">
          <div className="zone-details__info-box-content">
            <div className="zone-details__info-box-title">AI Insights</div>
            <p className="zone-details__info-box-text zone-details__info-box-text--analysis">
              {displayedAnalysisText || 'AI Insights are not available at the moment.'}
            </p>
          </div>
        </div>
      </div>

      {/* Object Level Sensors */}
      {objectSensors.length > 0 && (
        <>
          <div className="zone-details__section-header">
            <div className="zone-details__section-header-icon zone-details__section-header-icon--green">
              <ObjectIcon />
            </div>
            <div className="zone-details__section-header-text">
              <div className="zone-details__section-header-title">Object Level Sensors</div>
              <div className="zone-details__section-header-subtitle">
                Sensors monitoring the entire object
              </div>
            </div>
          </div>

          {[...(objectSensors || [])]
            .sort(
              (a, b) =>
                parseInt(a.sensor_type.split('_')?.[1]?.slice(1) || '0') -
                parseInt(b.sensor_type.split('_')?.[1]?.slice(1) || '0'),
            )
            .map((sensor) => (
              <SensorCard key={sensor.id} sensorId={sensor.id} onHourClick={handleHourClick} />
            ))}
        </>
      )}

      {/* Zone Level Sensors */}
      {zoneSensors.length > 0 && (
        <>
          <div className="zone-details__section-header">
            <div className="zone-details__section-header-icon zone-details__section-header-icon--blue">
              <ZoneIcon />
            </div>
            <div className="zone-details__section-header-text">
              <div className="zone-details__section-header-title">Zone Level Sensors</div>
              <div className="zone-details__section-header-subtitle">
                Sensors specific to this zone
              </div>
            </div>
          </div>

          {[...(zoneSensors || [])]
            .sort(
              (a, b) =>
                parseInt(a.sensor_type.split('_')?.[1]?.slice(1) || '0') -
                parseInt(b.sensor_type.split('_')?.[1]?.slice(1) || '0'),
            )
            .map((sensor) => (
              <SensorCard key={sensor.id} sensorId={sensor.id} onHourClick={handleHourClick} />
            ))}
        </>
      )}
    </div>
  );
};

export default ZoneDetails;
